/**
 * Pure function that builds an EntityRegistry from existing store slices.
 *
 * One loop over blend objects, one over scene objects → flat Map.
 * Component data holds references to existing objects, not copies.
 */

import { buildObjectCollectionIndex } from "../app/collectionGraph.ts";
import { composeGroupTransform } from "../app/groupTransform.ts";
import type {
  BlendFileEntry,
  Collection,
  ModifierOverride,
} from "../app/storeTypes.ts";
import type { BlendFile } from "../blend-reader/types/BlendFile.ts";
import { ObjectType } from "../blend-reader/types/BlendObject.ts";
import type {
  MaterialDataBlock,
  SceneMaterial,
  SceneObject,
} from "../modeling/types.ts";
import { DEFAULT_MATERIAL, getPrimaryMaterialId } from "../modeling/types.ts";
import { blendMaterialToScene } from "./blendMaterialToScene.ts";
import type {
  ComponentTypeMap,
  CurveComponent,
  EntityKind,
  HierarchyComponent,
  LightComponent,
  MannequinComponent,
  MaterialComponent,
  MeshInstanceComponent,
  ModifierStackComponent,
  ShapeKeyComponent,
  TransformComponent,
  VisibilityComponent,
} from "./components.ts";
import type { Entity, EntityRegistry } from "./Entity.ts";
import type { EntityId } from "./EntityId.ts";
import { blendEntityId, sceneEntityId } from "./EntityId.ts";

// ---------------------------------------------------------------------------
// Input slice — the subset of AppState the registry depends on
// ---------------------------------------------------------------------------

export interface RegistryInput {
  blendFiles: ReadonlyMap<string, BlendFileEntry>;
  sceneObjects: ReadonlyMap<string, SceneObject>;
  materials: ReadonlyMap<string, MaterialDataBlock>;
  collections: ReadonlyMap<string, Collection>;
  visibility: ReadonlyMap<string, boolean>;
  modifierOverrides: ReadonlyMap<string, readonly ModifierOverride[]>;
  shapeKeyWeights: ReadonlyMap<string, ReadonlyMap<string, number>>;
  mannequinMorphs: ReadonlyMap<string, ReadonlyMap<string, number>>;
  mannequinBonePoses: ReadonlyMap<
    string,
    ReadonlyMap<string, readonly [number, number, number, number]>
  >;
}

// ---------------------------------------------------------------------------
// Kind mapping
// ---------------------------------------------------------------------------

function blendObjectKind(type: ObjectType): EntityKind {
  switch (type) {
    case ObjectType.Mesh:
      return "mesh";
    case ObjectType.Light:
      return "light";
    case ObjectType.Camera:
      return "camera";
    case ObjectType.Armature:
      return "armature";
    default:
      return "empty";
  }
}

function sceneObjectKind(objectKind: string): EntityKind {
  switch (objectKind) {
    case "point_light":
    case "directional_light":
      return "light";
    case "mannequin":
      return "mannequin";
    case "curve":
      return "curve";
    case "empty":
      return "empty";
    case "image_plane":
      return "image_plane";
    default:
      return "mesh";
  }
}

// ---------------------------------------------------------------------------
// Material resolution for blend objects
// ---------------------------------------------------------------------------

/**
 * Resolve a blend object's first material to a SceneMaterial.
 * Falls back to DEFAULT_MATERIAL when no material is assigned.
 */
function resolveBlendMaterial(
  blendFile: BlendFile,
  meshRef: string | null,
): SceneMaterial {
  if (!meshRef) return DEFAULT_MATERIAL;
  const mesh = blendFile.meshes.get(meshRef);
  if (!mesh || mesh.materialPtrs.length === 0) return DEFAULT_MATERIAL;
  const ptr = mesh.materialPtrs[0];
  const mat = blendFile.materials.get(ptr);
  if (!mat) return DEFAULT_MATERIAL;
  return blendMaterialToScene(mat);
}

// ---------------------------------------------------------------------------
// Builder
// ---------------------------------------------------------------------------

const OB_RESTRICT_VIEWPORT = 1;

export function buildRegistry(input: RegistryInput): EntityRegistry {
  const map = new Map<EntityId, Entity>();
  const objectCollectionIndex = buildObjectCollectionIndex(input.collections);

  // -- Blend objects (iterate all entries) --
  for (const [entryId, entry] of input.blendFiles) {
    const bf = entry.file;
    for (const obj of bf.objects) {
      const eid = blendEntityId(obj.name);
      const kind = blendObjectKind(obj.type);
      const components: Partial<ComponentTypeMap> = {};

      // Transform — compose group transform with local transform for world-space position.
      // This ensures gizmo pivot, origin dots, and transform panel reflect
      // the actual rendered position after group moves.
      const worldT = composeGroupTransform(entry.groupTransform, {
        position: [...obj.localPosition] as [number, number, number],
        rotation: [...obj.localRotation] as [number, number, number],
        scale: [...obj.localScale] as [number, number, number],
      });
      components.transform = {
        position: worldT.position,
        rotation: worldT.rotation,
        scale: worldT.scale,
      } satisfies TransformComponent;

      // Hierarchy
      components.hierarchy = {
        parentId: obj.parentName ? blendEntityId(obj.parentName) : null,
        parentInverse: obj.parentInverse,
      } satisfies HierarchyComponent;

      // MeshInstance
      if (obj.meshRef) {
        components.meshInstance = {
          meshRef: obj.meshRef,
          source: "blend",
        } satisfies MeshInstanceComponent;
      }

      // Material (first slot)
      components.material = {
        material: resolveBlendMaterial(bf, obj.meshRef),
      } satisfies MaterialComponent;

      // ModifierStack
      const overrides = input.modifierOverrides.get(obj.name);
      if (obj.modifiers.length > 0 || (overrides && overrides.length > 0)) {
        components.modifierStack = {
          baseModifiers: obj.modifiers,
          overrides: overrides ?? [],
        } satisfies ModifierStackComponent;
      }

      // ShapeKey
      if (obj.shapeKeySetRef) {
        const weights = input.shapeKeyWeights.get(obj.shapeKeySetRef);
        components.shapeKey = {
          shapeKeySetRef: obj.shapeKeySetRef,
          weights: weights ?? new Map(),
        } satisfies ShapeKeyComponent;
      }

      // Visibility
      const visOverride = input.visibility.get(obj.name);
      components.visibility = {
        visible:
          visOverride ??
          (!(obj.restrictFlag & OB_RESTRICT_VIEWPORT) &&
            !obj.hiddenByCollection),
        restrictFlag: obj.restrictFlag,
        hiddenByCollection: obj.hiddenByCollection,
      } satisfies VisibilityComponent;

      map.set(eid, {
        id: eid,
        name: obj.name,
        kind,
        origin: "blend",
        blendFileId: entryId,
        components,
      });
    }
  }

  // -- Scene objects --
  for (const obj of input.sceneObjects.values()) {
    const eid = sceneEntityId(obj.id);
    const kind = sceneObjectKind(obj.objectKind);
    const components: Partial<ComponentTypeMap> = {};
    const collectionId = objectCollectionIndex.get(obj.id);
    const collectionTransform = collectionId
      ? input.collections.get(collectionId)?.transform
      : null;
    const worldT = collectionTransform
      ? composeGroupTransform(collectionTransform, {
          position: obj.position,
          rotation: obj.rotation,
          scale: obj.scale,
        })
      : {
          position: obj.position,
          rotation: obj.rotation,
          scale: obj.scale,
        };

    // Transform — scene objects report world transforms after collection parenting.
    components.transform = {
      position: worldT.position,
      rotation: worldT.rotation,
      scale: worldT.scale,
    } satisfies TransformComponent;

    // Hierarchy (scene objects have no parent yet)
    components.hierarchy = {
      parentId: null,
      parentInverse: null,
    } satisfies HierarchyComponent;

    // MeshInstance (for objects with geometry)
    if (kind === "mesh" || kind === "curve") {
      components.meshInstance = {
        meshRef: obj.id,
        source: "scene",
      } satisfies MeshInstanceComponent;
    }

    // Material (primary / slot-0 view — multi-slot routing happens in the
    // renderer, not at the ECS component layer).
    components.material = {
      material:
        input.materials.get(getPrimaryMaterialId(obj))?.properties ??
        DEFAULT_MATERIAL,
    } satisfies MaterialComponent;

    // Light
    if (kind === "light" && obj.light) {
      components.light = {
        lightKind: obj.objectKind === "point_light" ? "point" : "directional",
        color: obj.light.color,
        intensity: obj.light.intensity,
      } satisfies LightComponent;
    }

    // Curve
    if (kind === "curve" && obj.curveData) {
      components.curve = {
        curveData: obj.curveData,
      } satisfies CurveComponent;
    }

    // ModifierStack (scene objects only have runtime modifiers)
    const overrides = input.modifierOverrides.get(obj.id);
    if (overrides && overrides.length > 0) {
      components.modifierStack = {
        baseModifiers: [],
        overrides,
      } satisfies ModifierStackComponent;
    }

    // Mannequin
    if (kind === "mannequin") {
      const morphs = input.mannequinMorphs.get(obj.id);
      const bonePoses = input.mannequinBonePoses.get(obj.id);
      components.mannequin = {
        modelId: obj.mannequinModelId ?? "male-neutral",
        morphs: morphs ?? new Map(),
        bonePoses: bonePoses ?? new Map(),
      } satisfies MannequinComponent;
    }

    // Visibility
    const visOverride = input.visibility.get(obj.id);
    components.visibility = {
      visible: visOverride ?? true,
      restrictFlag: null,
      hiddenByCollection: false,
    } satisfies VisibilityComponent;

    map.set(eid, {
      id: eid,
      name: obj.name,
      kind,
      origin: "scene",
      components,
    });
  }

  return map;
}
